// Thin read-only client for the public CS2KZ API (https://api.cs2kz.org).
// No auth required for the endpoints we use. Docs: https://docs.cs2kz.org/api/
//
// Kept UI-agnostic on purpose: it returns plain data (or throws) so callers can
// own their own loading/error handling.

const API_BASE = 'https://api.cs2kz.org';

/**
 * The two CS2KZ movement modes. `id` is the value the API expects; `short` is
 * how the community writes them (ckz / vnl).
 */
export const KZ_MODES = [
  { id: 'classic', short: 'CKZ', label: 'Classic' },
  { id: 'vanilla', short: 'VNL', label: 'Vanilla' },
];

const getJson = async (path) => {
  const res = await fetch(`${API_BASE}${path}`);
  if (!res.ok) {
    throw new Error(`CS2KZ API ${res.status} for ${path}`);
  }
  return res.json();
};

/**
 * List approved maps, each with its courses and per-mode filters.
 * @returns {Promise<Array>} the `values` array from GET /maps
 */
export const fetchApprovedMaps = async () => {
  const data = await getJson('/maps?state=approved&limit=500');
  return Array.isArray(data?.values) ? data.values : [];
};

/**
 * Look up a single player by SteamID64 or legacy STEAM_ id.
 * @returns {Promise<object|null>} the player, or null if not found (404)
 */
export const fetchPlayer = async (steamId) => {
  try {
    return await getJson(`/players/${encodeURIComponent(steamId)}`);
  } catch (err) {
    if (String(err.message).includes('404')) return null;
    throw err;
  }
};

/**
 * When a record was submitted, in epoch milliseconds.
 *
 * The API exposes no submission timestamp on a record — but record ids are
 * UUIDv7, whose first 48 bits ARE the submission time in ms. Verified against
 * `sort_by=submission-date`: the decoded order matches exactly.
 *
 * @returns {number|null} epoch ms, or null if the id isn't a parseable UUIDv7
 */
export const recordSubmittedAt = (record) => {
  const hex = String(record?.id || '')
    .replace(/-/g, '')
    .slice(0, 12);
  const ms = Number.parseInt(hex, 16);
  return Number.isNaN(ms) ? null : ms;
};

// Paging for the run scan below. A player might grind a course all day, but
// 1000 runs on one course is far past any realistic session.
const RECORDS_PAGE_SIZE = 100;
const RECORDS_MAX_PAGES = 10;

/**
 * Fetch a player's fastest run on ONE course of a map, in one mode, submitted
 * inside a time window.
 *
 * Two filters matter here:
 *
 * - Course. A map's courses are separate runs with wildly different lengths
 *   (kz_topsecret: ~18s on "Hangar" vs ~148s on "Top Secret"), so an unfiltered
 *   query returns whichever course happens to be fastest and the times aren't
 *   comparable between players. The API matches the course by NAME
 *   (`course_id` is silently ignored).
 * - Time window. The API has no date filter and every run is stored, not just
 *   PBs — so an unbounded query returns a player's all-time PB, and a board for
 *   a one-hour session would show times from months ago. We instead walk the
 *   runs newest-first, skip anything after the window, and stop at the first
 *   one before it.
 *
 * @param {{ mapId: number, courseName: string, mode: string, steamId: string,
 *   since: number, until?: number }} args - `since`/`until` are epoch ms;
 *   `until` defaults to no upper bound
 * @returns {Promise<object|null>} their fastest run in the window, or null if
 *   they set none
 */
export const fetchBestRecordInWindow = async ({
  mapId,
  courseName,
  mode,
  steamId,
  since,
  until = Infinity,
}) => {
  let best = null;

  for (let page = 0; page < RECORDS_MAX_PAGES; page += 1) {
    const params = new URLSearchParams({
      map: String(mapId),
      course: courseName,
      mode,
      player: steamId,
      sort_by: 'submission-date',
      sort_order: 'descending',
      limit: String(RECORDS_PAGE_SIZE),
      offset: String(page * RECORDS_PAGE_SIZE),
    });
    // eslint-disable-next-line no-await-in-loop -- pages must be walked in order to stop early
    const data = await getJson(`/records?${params.toString()}`);
    const values = data?.values || [];

    for (let i = 0; i < values.length; i += 1) {
      const record = values[i];
      const submittedAt = recordSubmittedAt(record);
      // Newest-first, so the first run before the window ends the scan.
      if (submittedAt == null || submittedAt < since) return best;
      if (submittedAt > until) continue;
      if (!best || record.time < best.time) best = record;
    }

    if (values.length < RECORDS_PAGE_SIZE) break;
  }

  return best;
};

/** Steam Workshop page for a map, from its workshop_id. */
export const workshopUrl = (workshopId) =>
  `https://steamcommunity.com/sharedfiles/filedetails/?id=${workshopId}`;

// Community map screenshots, one per course, published by the KZ Global Team.
// The CS2KZ API itself serves no images. Paths are <size>/<map>/<course-number>,
// where the course number is the course's 1-based position in the map's course
// list.
const MAP_IMAGE_BASE = 'https://raw.githubusercontent.com/KZGlobalTeam/cs2kz-images/public/webp';

/**
 * Screenshot for one course of a map.
 * @param {string} mapName
 * @param {number} courseIndex - 1-based
 * @param {'thumbnail'|'medium'|'full'} [size]
 * @returns {string|null} null when we don't know which course to ask for
 */
export const mapImageUrl = (mapName, courseIndex, size = 'medium') =>
  mapName && courseIndex ? `${MAP_IMAGE_BASE}/${size}/${mapName}/${courseIndex}.webp` : null;
